import * as THREE from "three";
import Orchestrator from "../Orchestrator.js";
import { CONFIG } from "./config.js";
import sandParticleUrl from "./textures/sand-particle.png";

// The sprite is a square with two opposite corners cut out. Alpha is the mask;
// the flat gray is not a second sand color.
function createSandTexture() {
    const texture = new THREE.TextureLoader().load(sandParticleUrl);
    texture.colorSpace = THREE.NoColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    return texture;
}

// Equal shares of 0, 1 and 2, then shuffled so the colors are scattered.
function colorIndices(count) {
    const indices = new Float32Array(count);
    for (let n = 0; n < count; n++) indices[n] = n % 3;
    for (let n = count - 1; n > 0; n--) {
        const swap = Math.floor(Math.random() * (n + 1));
        const value = indices[n];
        indices[n] = indices[swap];
        indices[swap] = value;
    }
    return indices;
}

export default class Sandstorm {
    constructor(debugFolder) {
        this.orc = new Orchestrator();
        this.scene = this.orc.scene;
        this.debug = this.orc.debug;
        this.anchor = new THREE.Vector3();

        this.colorA = new THREE.Color("#f88859");
        this.colorB = new THREE.Color("#c8b193");
        this.colorC = new THREE.Color("#ecc38e");
        this._tintA = this.colorA.clone();
        this._tintB = this.colorB.clone();
        this._tintC = this.colorC.clone();
        this.params = {
            count: CONFIG.SAND_COUNT,
            opacity: 0.72,
            windSpeed: 1.4,
            speedVariation: 0.4,
            skewed: 0.1,
            skewAngle: 30,
            range: CONFIG.SAND_RANGE,
            top: CONFIG.SAND_TOP,
            sizeVariation: 0.6,
            textured: 0.4,
        };
        this.uniforms = {
            uColorA: { value: this._tintA },
            uColorB: { value: this._tintB },
            uColorC: { value: this._tintC },
            uSizeVariation: { value: this.params.sizeVariation },
        };

        this.geometry = new THREE.BufferGeometry();
        this.material = new THREE.PointsMaterial({
            color: 0xffffff,
            map: createSandTexture(),
            size: CONFIG.SAND_SIZE,
            sizeAttenuation: true,
            transparent: true,
            opacity: 0,
            depthWrite: false,
        });
        this.material.customProgramCacheKey = () => "sandstorm-particles";
        this.material.onBeforeCompile = (shader) => {
            shader.uniforms.uColorA = this.uniforms.uColorA;
            shader.uniforms.uColorB = this.uniforms.uColorB;
            shader.uniforms.uColorC = this.uniforms.uColorC;
            shader.uniforms.uSizeVariation = this.uniforms.uSizeVariation;

            shader.vertexShader = shader.vertexShader
                .replace(
                    "#include <common>",
                    `#include <common>
attribute float aSizeRand;
attribute float aColorIndex;
attribute float aSprite;
varying float vColorIndex;
varying float vSprite;
uniform float uSizeVariation;`,
                )
                .replace(
                    "gl_PointSize = size;",
                    `vColorIndex = aColorIndex;
vSprite = aSprite;
gl_PointSize = size * mix(1.0 - uSizeVariation, 1.0 + uSizeVariation, aSizeRand);`,
                );

            shader.fragmentShader = shader.fragmentShader
                .replace(
                    "#include <common>",
                    `#include <common>
varying float vColorIndex;
varying float vSprite;
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform vec3 uColorC;`,
                )
                .replace(
                    "vec4 diffuseColor = vec4( diffuse, opacity );",
                    `vec3 sandColor = uColorA;
if (vColorIndex > 1.5) sandColor = uColorC;
else if (vColorIndex > 0.5) sandColor = uColorB;
vec4 diffuseColor = vec4(sandColor, opacity);`,
                )
                .replace(
                    "#include <map_particle_fragment>",
                    `if (vSprite > 0.5) {
	vec2 sandUv = (uvTransform * vec3(gl_PointCoord.x, 1.0 - gl_PointCoord.y, 1.0)).xy;
	if (texture2D(map, sandUv).a < 0.5) discard;
}`,
                );
        };

        this.points = new THREE.Points(this.geometry, this.material);
        // The cloud follows the player, so bounds computed from the first frame go stale.
        this.points.frustumCulled = false;
        this.points.visible = false;
        this.scene.add(this.points);

        this.setCount(this.params.count);
        this.setDebug(debugFolder);
    }

    setCount(count) {
        const { range, top } = this.params;
        this.positions = new Float32Array(count * 3);
        this.velocities = new Float32Array(count * 3);
        this.speedRand = new Float32Array(count);
        this.skewed = new Float32Array(count);
        this.skewSign = new Float32Array(count);
        const sizeRand = new Float32Array(count);
        const colorIndex = colorIndices(count);
        const sprite = new Float32Array(count);

        for (let n = 0; n < count; n++) {
            this.positions[n * 3] =
                this.anchor.x + (Math.random() - 0.5) * range * 2;
            this.positions[n * 3 + 1] = this.anchor.y + Math.random() * top - 3;
            this.positions[n * 3 + 2] =
                this.anchor.z + (Math.random() - 0.5) * range * 2;
            this.velocities[n * 3] = 7 + Math.random() * 9;
            this.velocities[n * 3 + 1] = (Math.random() - 0.5) * 1.6;
            this.velocities[n * 3 + 2] = 2.5 + Math.random() * 5;
            this.speedRand[n] = Math.random();
            this.skewed[n] = Math.random() < this.params.skewed ? 1 : 0;
            this.skewSign[n] = Math.random() < 0.5 ? -1 : 1;
            sizeRand[n] = Math.random();
            sprite[n] = Math.random() < this.params.textured ? 1 : 0;
        }

        this.geometry.dispose();
        this.geometry.setAttribute(
            "position",
            new THREE.BufferAttribute(this.positions, 3),
        );
        this.geometry.setAttribute(
            "aSizeRand",
            new THREE.BufferAttribute(sizeRand, 1),
        );
        this.geometry.setAttribute(
            "aColorIndex",
            new THREE.BufferAttribute(colorIndex, 1),
        );
        this.geometry.setAttribute(
            "aSprite",
            new THREE.BufferAttribute(sprite, 1),
        );
    }

    setSkewShare(share) {
        if (!this.skewed) return;
        for (let n = 0; n < this.skewed.length; n++) {
            this.skewed[n] = Math.random() < share ? 1 : 0;
        }
    }

    setSpriteShare(share) {
        const attribute = this.geometry.getAttribute("aSprite");
        if (!attribute) return;
        for (let n = 0; n < attribute.count; n++) {
            attribute.setX(n, Math.random() < share ? 1 : 0);
        }
        attribute.needsUpdate = true;
    }

    setDebug(folder) {
        if (!folder) return;
        const particles = folder.addFolder("Particles");
        this.debug.addColor(particles, this.colorA, "color A");
        this.debug.addColor(particles, this.colorB, "color B");
        this.debug.addColor(particles, this.colorC, "color C");
        particles.add(this.params, "opacity", 0, 1, 0.01).name("opacity");
        particles.add(this.material, "size", 0.02, 2, 0.01).name("size");
        particles
            .add(this.params, "sizeVariation", 0, 1, 0.01)
            .name("size variation")
            .onChange((value) => {
                this.uniforms.uSizeVariation.value = value;
            });
        particles
            .add(this.params, "textured", 0, 1, 0.01)
            .name("textured")
            .onFinishChange((share) => this.setSpriteShare(share));
        particles
            .add(this.params, "count", 0, 10000, 50)
            .name("count")
            .onFinishChange((count) => this.setCount(count));
        particles.add(this.params, "windSpeed", 0, 5, 0.05).name("wind speed");
        particles
            .add(this.params, "speedVariation", 0, 1, 0.01)
            .name("speed variation");
        particles
            .add(this.params, "skewed", 0, 1, 0.01)
            .name("skewed")
            .onFinishChange((share) => this.setSkewShare(share));
        particles.add(this.params, "skewAngle", 0, 90, 1).name("skew angle");
        particles.add(this.params, "range", 5, 150, 1).name("spread");
        particles.add(this.params, "top", 2, 80, 1).name("height");
    }

    update(dt, anchor, intensity, nightScale = 1) {
        this.anchor.copy(anchor);
        this._tintA.copy(this.colorA).multiplyScalar(nightScale);
        this._tintB.copy(this.colorB).multiplyScalar(nightScale);
        this._tintC.copy(this.colorC).multiplyScalar(nightScale);
        this.material.opacity = intensity * this.params.opacity;
        this.points.visible = intensity > 0.01;
        if (!this.points.visible) return;

        const positions = this.positions;
        const velocities = this.velocities;
        const boost = (1 + intensity * 0.9) * this.params.windSpeed;
        const variation = this.params.speedVariation;
        const speedRand = this.speedRand;
        const skewed = this.skewed;
        const skewSign = this.skewSign;
        const cosA = Math.cos((this.params.skewAngle * Math.PI) / 180);
        const sinA = Math.sin((this.params.skewAngle * Math.PI) / 180);
        const { range, top } = this.params;
        const low = anchor.y - 8;
        const high = anchor.y + top;

        for (let index = 0; index < positions.length; index += 3) {
            const n = index / 3;
            let vx = velocities[index];
            let vz = velocities[index + 2];
            if (skewed[n]) {
                const sign = skewSign[n];
                const rx = vx * cosA - vz * sinA * sign;
                const rz = vx * sinA * sign + vz * cosA;
                vx = rx;
                vz = rz;
            }
            const speed = 1 - variation + speedRand[n] * variation * 2;
            positions[index] += vx * dt * boost * speed;
            positions[index + 1] += velocities[index + 1] * dt * speed;
            positions[index + 2] += vz * dt * boost * speed;

            if (positions[index] - anchor.x > range)
                positions[index] -= range * 2;
            if (positions[index] - anchor.x < -range)
                positions[index] += range * 2;
            if (positions[index + 2] - anchor.z > range) {
                positions[index + 2] -= range * 2;
            }
            if (positions[index + 2] - anchor.z < -range) {
                positions[index + 2] += range * 2;
            }

            if (positions[index + 1] < low) positions[index + 1] = high;
            if (positions[index + 1] > high) positions[index + 1] = low;
        }

        this.geometry.attributes.position.needsUpdate = true;
    }

    reset() {
        this.material.opacity = 0;
        this.points.visible = false;
    }
}
